import { api, type ApiScope } from "@/lib/apiClient";
import { sessionSchema, type LoginInput, type SessionResponse } from "./schemas";

/**
 * Chamadas de auth. Os dois escopos usam os mesmos verbos em prefixos
 * diferentes: usuario de clinica em /api/v1/auth, operator em
 * /api/v1/admin/auth. Auth de operator e outra tabela e outro cookie, nao um
 * papel a mais (CLAUDE.md 6).
 */
const paths = {
  user: {
    login: "/api/v1/auth/login",
    logout: "/api/v1/auth/logout",
    me: "/api/v1/auth/me",
    revokeAll: "/api/v1/auth/sessions",
  },
  operator: {
    login: "/api/v1/admin/auth/login",
    logout: "/api/v1/admin/auth/logout",
    me: "/api/v1/admin/auth/me",
    revokeAll: "/api/v1/admin/auth/sessions",
  },
} as const;

/** Valida a resposta com Zod: contrato quebrado no backend precisa falhar
 *  alto aqui, e nao virar `undefined` tres componentes adiante. */
export async function login(input: LoginInput, scope: ApiScope = "user"): Promise<SessionResponse> {
  const { data } = await api.post<unknown>(paths[scope].login, input, { scope });
  return sessionSchema.parse(data);
}

export async function fetchSession(scope: ApiScope = "user"): Promise<SessionResponse> {
  const { data } = await api.get<unknown>(paths[scope].me, { scope });
  return sessionSchema.parse(data);
}

export async function logout(scope: ApiScope = "user"): Promise<void> {
  await api.delete(paths[scope].logout, { scope });
}

export async function revokeAllSessions(scope: ApiScope = "user"): Promise<void> {
  await api.delete(paths[scope].revokeAll, { scope });
}

/** Aceite do termo de consentimento LGPD (RF-02.6). So existe no escopo de
 *  usuario — operator nunca consente em nome de ninguem. */
export async function acceptConsent(): Promise<{ consent_given: boolean; consent_version: string }> {
  const { data } = await api.post<{ consent_given: boolean; consent_version: string }>("/api/v1/consent");
  return data;
}

/** RF-02.5: pede o e-mail de reset (ou reenvio de convite, se a conta ainda
 *  nao foi ativada — quem decide isso e o backend). Sempre 202 neutro. */
export async function requestPasswordReset(email: string): Promise<void> {
  await api.post("/api/v1/auth/password/reset", { email });
}

/** RF-02.4/02.5: confirma o token e define a senha. Mesmo endpoint para
 *  convite e para reset — o backend distingue pelo `purpose` do token. */
export async function confirmPasswordReset(token: string, password: string): Promise<SessionResponse> {
  const { data } = await api.post<unknown>("/api/v1/auth/password/reset/confirm", { token, password });
  return sessionSchema.parse(data);
}

/** "Esqueci minha clinica": e-mail digitado na raiz do dominio, sem
 *  subdominio. Sempre 202 neutro (RNF-02.4) — o backend nunca revela se o
 *  e-mail existe nem em qual(is) tenant(s); a lista de clinicas so chega
 *  por e-mail assincrono, nunca nesta resposta. */
export async function findTenant(email: string): Promise<void> {
  await api.post("/api/v1/auth/find_tenant", { email });
}
